import * as React from 'react'

import {
  UserListItem,
  UserListRequestPayload,
  UserRoleAssignment,
  useMutationSendInvitation,
  useMutationUpdateUserRoles,
  useQueryAdminUsers,
} from '@/lib/api/company/configuration/adminPanel/users'
import { useTable } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { CheckboxField } from '@/modules/shared/components'
import { Loader } from '@/ui'
import { Pagination } from '@/modules/shared/components/Pagination'
import { toast } from '@/lib/toasts/api'
import { AdminUserRow } from './AdminUserRow'
import {
  ServiceAccountUsersNotice,
  useServiceAccountUsersContext,
} from '@/modules/shared/components/serviceAccount/ServiceAccountUserRoles'
import { ROUTES } from 'app/constants/routes'
import { isAdmin } from 'app/services/local/roleService'

type Props = {
  /** Pusty = widok globalny (wszystkie firmy), tak jak panel otwarty bez firmy. */
  companyId?: string
  onEdit: (user: UserListItem) => void
}

const PER_PAGE_DEFAULT = 10

type Column = { key: string; label: string; sortable?: boolean }

/**
 * Lista użytkowników panelu admina na zapytaniu z `@/lib/api` i komponentach z `/et`.
 *
 * Zastępuje tabelę react-table v6 z `app/components/administration/users.tsx`: sortowanie
 * po nagłówku, stronicowanie z `@/ui`, zaznaczanie przez `TableContext`. Filtry trzyma
 * rodzic, bo dzieli je z paskiem akcji.
 */
export const AdminUsersTable: React.FC<
  Props & {
    filters: UserListRequestPayload
    setFilters: React.Dispatch<React.SetStateAction<UserListRequestPayload>>
  }
> = ({ companyId, onEdit, filters, setFilters }) => {
  const { t } = useLocalize()
  const { selectedGroup, toggleAll, setAllIds } = useTable()

  const query = useQueryAdminUsers({
    companyId: companyId ?? '',
    payload: filters,
  })
  // Stała referencja - `?? []` przy każdym renderze tworzyło nową tablicę, przez co efekt
  // z `setAllIds` wpadał w pętlę (`Maximum update depth exceeded`).
  const users = React.useMemo(() => query.data?.users ?? [], [query.data])
  // Endpoint oddaje `usersCount`; `totalCount` z typu `/et` nigdy nie przychodzi (tamta
  // lista nie ma stronicowania, więc nikt tego nie zauważył).
  const total =
    (query.data as { usersCount?: number })?.usersCount ??
    query.data?.totalCount ??
    0

  const serviceAccount = useServiceAccountUsersContext(companyId)
  const mutationRoles = useMutationUpdateUserRoles(companyId ?? '')
  const mutationInvitation = useMutationSendInvitation(companyId)

  React.useEffect(() => {
    setAllIds(users.map((user) => ({ id: user.id, isActive: true })))
    // `setAllIds` pochodzi z kontekstu i zmienia tożsamość przy każdym renderze, więc
    // trzymamy zależność tylko na danych.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users])

  const handleRolesUpdate = React.useCallback(
    async (user: UserListItem, roles: UserRoleAssignment[]) => {
      await mutationRoles.mutateAsync({ user, roles, filters })
    },
    [mutationRoles, filters],
  )

  const handleInvitation = React.useCallback(
    async (user: UserListItem) => {
      try {
        await mutationInvitation.mutateAsync(user.login)
        toast.success({
          title: t('administrationPanel-users.activation-sent-title'),
        })
      } catch {
        toast.error({ title: t('administrationPanel-users.activation-error') })
      }
    },
    [mutationInvitation, t],
  )

  const sort = (key: string) =>
    setFilters((prev) => ({
      ...prev,
      orderBy: key,
      descending: prev.orderBy === key ? !prev.descending : true,
      skip: 0,
    }))

  const columns: Column[] = [
    {
      key: 'locked',
      label: t('administrationPanel-users.status'),
      sortable: true,
    },
    {
      key: 'firstName',
      label: t('administrationPanel-users.user'),
      sortable: true,
    },
    { key: 'companyName', label: t('administrationPanel-users.company') },
    { key: 'role', label: t('administrationPanel-users.role') },
    {
      key: 'login',
      label: t('administrationPanel-users.login'),
      sortable: true,
    },
    { key: 'createdBy', label: t('auditFields.created-by') },
    {
      key: 'createdDate',
      label: t('auditFields.created-date'),
      sortable: true,
    },
  ]

  const pageSize = filters.take ?? PER_PAGE_DEFAULT
  const currentPage = Math.floor((filters.skip ?? 0) / pageSize) + 1
  const allChecked =
    users.length > 0 && selectedGroup?.ids.length === users.length

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      {serviceAccount.isService && (
        <ServiceAccountUsersNotice
          href={
            isAdmin()
              ? `${ROUTES.AdministrationPanel.NoCompanyTab.replace(':tab', 'serviceAccount')}?account=${companyId}`
              : undefined
          }
        />
      )}
      <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
        <table className="tw-w-full tw-border-collapse">
          <thead className="tw-sticky tw-top-0 tw-z-10 tw-bg-content-primary">
            <tr>
              <th className="tw-w-12 tw-border-b tw-border-base-300 tw-px-4 tw-py-3">
                <CheckboxField
                  id="admin-users-all"
                  checked={allChecked}
                  onChange={(event) => toggleAll(event.target.checked, true)}
                />
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={column.sortable ? () => sort(column.key) : undefined}
                  className={`tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80 ${
                    column.sortable
                      ? 'tw-cursor-pointer hover:tw-bg-base-200'
                      : ''
                  }`}
                >
                  {column.label}
                  {column.sortable && filters.orderBy === column.key && (
                    <span className="tw-ml-1 tw-text-primary">
                      {filters.descending ? '↓' : '↑'}
                    </span>
                  )}
                </th>
              ))}
              <th className="tw-border-b tw-border-base-300" colSpan={2} />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <AdminUserRow
                key={user.id}
                user={user}
                companyId={companyId ?? ''}
                onRolesUpdate={handleRolesUpdate}
                onEdit={onEdit}
                onSendInvitation={handleInvitation}
                serviceAccount={
                  serviceAccount.isService
                    ? { membership: serviceAccount.rosterById.get(user.id) }
                    : undefined
                }
              />
            ))}
          </tbody>
        </table>

        {query.isLoading && (
          <div className="tw-flex tw-h-64 tw-items-center tw-justify-center">
            <Loader size="lg" />
          </div>
        )}
        {!query.isLoading && users.length === 0 && (
          <div className="tw-text-content-muted tw-flex tw-h-64 tw-items-center tw-justify-center">
            {t('common.no-data')}
          </div>
        )}
      </div>

      <div className="tw-flex-shrink-0 tw-border-t tw-border-base-300 tw-bg-content-primary">
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          pageSize={pageSize}
          onPageChange={(page) =>
            setFilters((prev) => ({ ...prev, skip: (page - 1) * pageSize }))
          }
          onPageSizeChange={(size) =>
            setFilters((prev) => ({ ...prev, take: size, skip: 0 }))
          }
        />
      </div>
    </div>
  )
}
